import { useState, useEffect } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { motion, AnimatePresence } from "framer-motion";

export const VideoShowcase = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      id="discover"
      data-testid="video-showcase-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-[#030712]"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background with Multicultural Composite */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Image Grid Composite */}
        <div className="absolute inset-0 grid grid-cols-3 grid-rows-2 gap-1 opacity-50">
          {/* Top row - 3 images */}
          <div 
            className="bg-cover bg-center transition-transform duration-[3s] ease-out"
            style={{
              backgroundImage: 'url("https://images.unsplash.com/photo-1573496130141-209d200cebd8?w=800")',
              transform: isHovered ? 'scale(1.05)' : 'scale(1.1)',
            }}
          />
          <div 
            className="bg-cover bg-center transition-transform duration-[3s] ease-out"
            style={{
              backgroundImage: 'url("https://images.pexels.com/photos/7698712/pexels-photo-7698712.jpeg?auto=compress&cs=tinysrgb&w=800")',
              transform: isHovered ? 'scale(1.08)' : 'scale(1.12)',
            }}
          />
          <div 
            className="bg-cover bg-center transition-transform duration-[3s] ease-out"
            style={{
              backgroundImage: 'url("https://images.unsplash.com/photo-1560250097-0b93528c311a?w=800")',
              transform: isHovered ? 'scale(1.03)' : 'scale(1.08)',
            }}
          />
          {/* Bottom row - 3 images */}
          <div 
            className="bg-cover bg-center transition-transform duration-[3s] ease-out"
            style={{
              backgroundImage: 'url("https://images.pexels.com/photos/6950050/pexels-photo-6950050.jpeg?auto=compress&cs=tinysrgb&w=800")',
              transform: isHovered ? 'scale(1.06)' : 'scale(1.1)',
            }}
          />
          <div 
            className="bg-cover bg-center transition-transform duration-[3s] ease-out"
            style={{
              backgroundImage: 'url("https://images.pexels.com/photos/12396627/pexels-photo-12396627.jpeg?auto=compress&cs=tinysrgb&w=800")',
              transform: isHovered ? 'scale(1.04)' : 'scale(1.09)',
            }}
          />
          <div 
            className="bg-cover bg-center transition-transform duration-[3s] ease-out"
            style={{
              backgroundImage: 'url("https://images.pexels.com/photos/6077567/pexels-photo-6077567.jpeg?auto=compress&cs=tinysrgb&w=800")',
              transform: isHovered ? 'scale(1.07)' : 'scale(1.11)',
            }}
          />
        </div>

        {/* Dark overlay to blend images */}
        <div className="absolute inset-0 bg-[#030712]/50" />

        {/* Cinematic Overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#030712]/90 via-[#030712]/40 to-[#030712]/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-transparent to-[#030712]/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#030712]" />
        
        {/* Premium Vignette */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 40%, rgba(3, 7, 18, 0.8) 100%)'
          }}
        />

        {/* Ambient Light Effects */}
        <div className="absolute top-1/4 left-0 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[200px] pointer-events-none animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-secondary/8 rounded-full blur-[150px] pointer-events-none" />

        {/* Film Grain Overlay */}
        <div 
          className="absolute inset-0 opacity-[0.02] pointer-events-none mix-blend-overlay"
          style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
          }}
        />
      </div>

      {/* Content Container */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-32 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <AnimatePresence>
            {showContent && (
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
                className="space-y-8"
              >
                {/* Premium Badge */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.8 }}
                  className="inline-flex items-center gap-3"
                >
                  <div className="w-12 h-[1px] bg-gradient-to-r from-primary to-transparent" />
                  <span className="text-primary/90 text-sm font-medium tracking-[0.3em] uppercase">
                    Exclusive Experience
                  </span>
                </motion.div>

                {/* Main Heading */}
                <motion.h2
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                  className="font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1]"
                >
                  <span className="text-white">Discover Your</span>
                  <br />
                  <span className="gradient-text">Global Potential</span>
                </motion.h2>

                {/* Elegant Description */}
                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7, duration: 0.8 }}
                  className="text-lg md:text-xl text-white/60 leading-relaxed max-w-lg font-light"
                >
                  Step into a world of limitless opportunity. Our curated programs 
                  connect exceptional individuals with transformative educational 
                  experiences across the globe.
                </motion.p>

                {/* Premium Feature Pills */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9, duration: 0.8 }}
                  className="flex flex-wrap gap-3"
                >
                  {['Elite Institutions', 'Global Network', 'Personal Guidance'].map((feature, idx) => (
                    <div
                      key={idx}
                      className="px-4 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm"
                    >
                      <span className="text-white/70 text-sm font-medium">{feature}</span>
                    </div>
                  ))}
                </motion.div>

                {/* CTA Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.1, duration: 0.8 }}
                  className="flex flex-col sm:flex-row gap-4 pt-4"
                >
                  <a href="#contact">
                    <Button
                      data-testid="video-showcase-cta"
                      className="group relative bg-primary text-[#030712] hover:bg-primary-hover rounded-full px-8 py-6 text-lg font-semibold transition-all duration-500 overflow-hidden"
                    >
                      <span className="relative z-10 flex items-center gap-2">
                        <Sparkles className="w-5 h-5" />
                        Start Your Journey
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-primary via-white/20 to-primary opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </Button>
                  </a>
                  <a href="#services">
                    <Button
                      variant="outline"
                      className="border border-white/20 text-white hover:bg-white/10 hover:border-white/40 rounded-full px-8 py-6 text-lg font-medium transition-all duration-300 bg-transparent backdrop-blur-sm"
                    >
                      Explore Programs
                    </Button>
                  </a>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Right Side - Floating Stats Card */}
          <AnimatePresence>
            {showContent && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9, x: 50 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                transition={{ delay: 1.3, duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
                className="hidden lg:block"
              >
                <div className="relative">
                  {/* Glowing Background */}
                  <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-3xl blur-2xl opacity-50" />
                  
                  {/* Glass Card */}
                  <div className="relative glass-card rounded-3xl p-8 border border-white/10">
                    <div className="space-y-6">
                      {/* Card Header */}
                      <div className="flex items-center justify-between">
                        <h3 className="text-white font-heading font-bold text-xl">Your Path Awaits</h3>
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          <Sparkles className="w-5 h-5 text-primary" />
                        </div>
                      </div>

                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 gap-4">
                        {[
                          { value: '98%', label: 'Success Rate' },
                          { value: '50+', label: 'Countries' },
                          { value: '200+', label: 'Partners' },
                          { value: '24/7', label: 'Support' },
                        ].map((stat, idx) => (
                          <div key={idx} className="p-4 rounded-xl bg-white/5 border border-white/5">
                            <div className="text-2xl font-heading font-black text-primary">{stat.value}</div>
                            <div className="text-white/50 text-sm">{stat.label}</div>
                          </div>
                        ))}
                      </div>

                      {/* Trust Indicator */}
                      <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                        <div className="flex -space-x-2">
                          {[
                            'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=60',
                            'https://images.pexels.com/photos/12396627/pexels-photo-12396627.jpeg?w=60',
                            'https://images.pexels.com/photos/6077567/pexels-photo-6077567.jpeg?w=60',
                          ].map((img, idx) => (
                            <img
                              key={idx}
                              src={img}
                              alt="Alumni"
                              className="w-8 h-8 rounded-full border-2 border-[#030712] object-cover"
                            />
                          ))}
                        </div>
                        <p className="text-white/60 text-sm">
                          <span className="text-primary font-semibold">10,000+</span> successful alumni worldwide
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-white/40 text-xs tracking-widest uppercase">Scroll</span>
          <div className="w-[1px] h-12 bg-gradient-to-b from-white/40 to-transparent" />
        </div>
      </motion.div>

      {/* Corner Decorations */}
      <div className="absolute top-0 left-0 w-32 h-32 pointer-events-none">
        <div className="absolute top-8 left-8 w-16 h-[1px] bg-gradient-to-r from-primary/50 to-transparent" />
        <div className="absolute top-8 left-8 w-[1px] h-16 bg-gradient-to-b from-primary/50 to-transparent" />
      </div>
      <div className="absolute bottom-0 right-0 w-32 h-32 pointer-events-none">
        <div className="absolute bottom-8 right-8 w-16 h-[1px] bg-gradient-to-l from-secondary/50 to-transparent" />
        <div className="absolute bottom-8 right-8 w-[1px] h-16 bg-gradient-to-t from-secondary/50 to-transparent" />
      </div>
    </section>
  );
};
